import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  Brand,
  ChangeEventType,
  StripeCreateSetupIntentForm,
  StripeCustomerBalanceCredit,
  StripeCustomerForm,
} from '../../../types';
import { CreateCampaignForm } from '../../../types/IDiy';
import { MuiTelInput } from 'mui-tel-input';
import { PrimaryButton } from '../../Buttons';
import CountrySelectMenu from '../../Select/CountrySelectMenu';
import AddCardIcon from '@mui/icons-material/AddCard';
import StripePaymentTermsModal from '../../Modal/StripePaymentTermsModal';
import { createStripeSetupIntent } from '../../../services/stripe/paymentIntent';
import {
  createOrUpdateStripeCustomer,
  fetchSingleStripeCustomer,
  getCustomerCreditBalances,
  getCustomerPaymentMethods,
} from '../../../services/stripe/customer';
import { useDispatch, useSelector } from 'react-redux';
import { setBrand, setLocation, toggleAlert } from '../../../redux/actions';
import StripePaymentUIModal from '../../Modal/StripePaymentUIModal';
import { DEFAULT_COLOR_THEME } from '../../../utils';
import { IconContext } from 'react-icons';
import {
  renderCCIcon,
  buildCardBrand,
} from '../../Cards/Billing/PaymentMethodHelpers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularLoading from '../../CircularLoading';
import Spinner from '../../Spinner';
import { toCurrency } from '../../../utils/numberFormatter';
import PoweredByStripe from '../../../assets/images/powered-by-stripe.svg';
import { dayJsInstance } from '../../../utils/dayjsHelper';
import { adsProvider } from '../../../utils/constants/facebookAds';
import { getSingleAdBudget } from '../../../services/budget';
import { errorMessageParser } from '../../../utils/helpers/ToastHelper';

interface DiyBillingSummaryV2Props {
  brand: Brand;
  location: Brand;
  formValues: CreateCampaignForm;
  setFormValues: Dispatch<SetStateAction<CreateCampaignForm>>;
  currentMonthlyBudget?: number;
  confirmPayment: boolean;
  setConfirmPayment: Dispatch<SetStateAction<boolean>>;
  selectedCampaign?: any;
}

const DiyBillingSummaryV2: FC<DiyBillingSummaryV2Props> = ({
  brand,
  location,
  formValues,
  setFormValues,
  currentMonthlyBudget = 0,
  confirmPayment,
  setConfirmPayment,
  selectedCampaign = null,
}) => {
  const timezone: string = useSelector(
    (state: any) => state?.brandTimezone?.timezone,
  );
  const isCampaignLevelBudget =
    formValues?.campaignTemplate?.budgetLevel === 'campaign';
  const isIncrease =
    (isCampaignLevelBudget
      ? formValues?.campaignTemplate
      : formValues?.adSetTemplate
    )?.totalBudget > currentMonthlyBudget;
  const isNoCharge =
    (isCampaignLevelBudget
      ? formValues?.campaignTemplate
      : formValues?.adSetTemplate
    )?.totalCharge === 0;

  const dispatch = useDispatch();
  const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('');
  const [customerForm, setCustomerForm] = useState<StripeCustomerForm>({
    address: {
      city: '',
      country: 'US',
      line1: '',
      line2: '',
      postal_code: '',
      state: '',
    },
    email: '',
    name: '',
    phone: '',
    metadata: {
      user_id: (location || brand)?._id,
      role: 'brand',
    },
  });
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [customer, setCustomer] = useState<any>(null);
  const [createCustomerLoading, setCreateCustomerLoading] =
    useState<boolean>(false);
  const [creditBalance, setCreditBalance] =
    useState<StripeCustomerBalanceCredit>(null);
  const [currentCampaignBudget, setCurrentCampaignBudget] = useState<number>(0);
  const [futureCampaignBudget, setFutureCampaignBudget] = useState<number>(0);

  useEffect(() => {
    if (location || brand) {
      getSingleStripeCustomer();
      getCustomerCredits();
    }
  }, [brand, location]);

  useEffect(() => {
    if (customer && (location || brand)) getPaymentMethods();
  }, [customer, location, brand]);

  useEffect(() => {
    if (isNoCharge && isCampaignLevelBudget && selectedCampaign) {
      getCampaignBudgets();
    }
  }, [isNoCharge, isCampaignLevelBudget, selectedCampaign]);

  const getSingleStripeCustomer = async () => {
    try {
      setLoading(true);

      const { data } = await fetchSingleStripeCustomer(
        (location || brand)?._id,
        'brand',
      );

      setCustomer(data);
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethods = async () => {
    try {
      setPaymentLoading(true);

      const response = await getCustomerPaymentMethods(
        (location || brand)?._id,
      );

      let temp: any = [...(response?.data || [])];
      temp = temp.sort((a: any) => {
        if (customer.invoice_settings.default_payment_method === a.id) {
          return -1;
        }

        return 1;
      });

      setPaymentMethods(temp);

      if (temp.length > 0) {
        setFormValues({ ...formValues, paymentMethod: temp[0].id });
      }
    } catch (error: any) {
      setPaymentMethods([]);

      console.log(error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const getCustomerCredits = async () => {
    try {
      const response = await getCustomerCreditBalances(
        (location || brand)?._id,
      );

      if (response.data.length > 0) {
        setCreditBalance(response.data[0]);
      }
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const getCampaignBudgets = async () => {
    try {
      const currentDate = dayJsInstance().tz(timezone);
      const currentMonth = currentDate.clone().month() + 1;
      const currentYear = currentDate.clone().year();
      let futureMonth = currentMonth + 1;
      let futureYear = currentYear;
      if (futureMonth > 12) {
        futureMonth = 1;
        futureYear = futureYear + 1;
      }
      let budgetParams: any = {
        brandId: (location || brand)?._id,
        month: currentMonth,
        year: currentYear,
        provider: adsProvider.FACEBOOK,
        campaignId: selectedCampaign?._id,
      };

      const currentBudget = await getSingleAdBudget(budgetParams);

      setCurrentCampaignBudget(currentBudget?.data?.totalBudget || 0);

      const futureBudget = await getSingleAdBudget({
        ...budgetParams,
        month: futureMonth,
        year: futureYear,
      });

      setFutureCampaignBudget(futureBudget?.data?.totalBudget || 0);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      setCreateCustomerLoading(true);

      const params: StripeCustomerForm = {
        ...customerForm,
        metadata: {
          user_id: (location || brand)?._id,
          role: 'brand',
        },
      };
      const customerData = await createOrUpdateStripeCustomer(
        params,
        (location || brand)?._id,
        'brand',
      );

      if (customerData?.data) {
        let temp: Brand = {
          ...(location || brand),
          stripe: {
            ...(location || brand)?.stripe,
            customerId: customerData.data.id,
          },
        };

        if (location) {
          dispatch(setLocation(temp));
        } else {
          dispatch(setBrand(temp));
        }

        const params: StripeCreateSetupIntentForm = {
          customer: customerData.data.id,
          payment_method_types: ['card'],
          metadata: {
            user_id: (location || brand)?._id,
            role: 'brand',
            action: 'payment_method',
          },
        };

        const setupIntent = await createStripeSetupIntent(
          params,
          (location || brand)?._id,
        );

        handleOpenTermsModal();
        handleOpenPaymentModal(setupIntent.data.client_secret, 'setup');
      }
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setCreateCustomerLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setLoading(true);

      const params: StripeCreateSetupIntentForm = {
        customer: (location || brand)?.stripe?.customerId,
        payment_method_types: ['card'],
        metadata: {
          user_id: (location || brand)?._id,
          role: 'brand',
          action: 'payment_method',
        },
      };

      const setupIntent = await createStripeSetupIntent(
        params,
        (location || brand)?._id,
      );

      handleOpenPaymentModal(setupIntent.data.client_secret, 'setup');
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleOnChangeCustomerForm = (e: ChangeEventType) => {
    const isAddress = [
      'city',
      'country',
      'line1',
      'line2',
      'postal_code',
      'state',
    ].includes(e.target.name);

    if (isAddress) {
      setCustomerForm({
        ...customerForm,
        address: {
          ...customerForm?.address,
          [e.target.name]: e.target.value,
        },
      });
    } else {
      setCustomerForm({
        ...customerForm,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleOpenTermsModal = () => {
    setOpenTermsModal((prev) => !prev);
  };

  const handleOpenPaymentModal = async (
    clientSecret: string,
    transactionType: string,
  ) => {
    setClientSecret(clientSecret);
    setOpenPaymentModal(true);
    setTransactionType(transactionType);
  };

  const handleClosePaymentModal = () => {
    setOpenPaymentModal(false);
    getPaymentMethods();
  };

  const buildChargingPlatform = () => {
    if (!isIncrease) {
      if (!(location || brand)?.chargeMediaFee) {
        return 'Decreased by Facebook';
      } else {
        return 'Credit will apply to the next invoice';
      }
    } else {
      if (!(location || brand)?.chargeMediaFee) {
        return 'Charged by Facebook';
      } else {
        return 'Charged Immediately';
      }
    }
  };

  const calculatedTotalAmount = () => {
    let budget: number = 0;
    if (!(location || brand)?.chargeMediaFee) {
      budget =
        (isCampaignLevelBudget
          ? formValues?.campaignTemplate
          : formValues?.adSetTemplate
        )?.totalCharge *
        ((location || brand)?.budgetPercentageFee / 100);
    } else {
      budget = (
        isCampaignLevelBudget
          ? formValues?.campaignTemplate
          : formValues?.adSetTemplate
      )?.totalCharge;
    }

    if (!isIncrease) {
      budget = Math.abs(budget);
    }

    return budget;
  };

  const calculatedAppliedBalance = () => {
    let appliedBalance: number = 0;
    if (creditBalance?.ending_balance < 0) {
      if (
        calculatedTotalAmount() > Math.abs(creditBalance?.ending_balance / 100)
      ) {
        appliedBalance = Math.abs(creditBalance?.ending_balance / 100);
      } else {
        return calculatedTotalAmount();
      }
    }

    return appliedBalance;
  };

  const buildPaymentAgreementLabel = () => {
    let amount: number = 0;
    let chargeText: string = '';

    if (calculatedAppliedBalance() > 0) {
      amount = calculatedTotalAmount() - calculatedAppliedBalance();
    } else {
      amount = calculatedTotalAmount();
    }

    if (amount === 0) {
      chargeText = `use my account credit of ${toCurrency(
        'USD',
        calculatedAppliedBalance(),
      )}`;
    } else {
      chargeText = `be charged ${toCurrency('USD', amount)} immediately`;
    }

    return `I confirm that I will ${chargeText} and I authorize this payment. And I authorize the budget ${
      !isIncrease ? 'decrease' : 'increase'
    } of the ad campaign on Facebook.`;
  };

  return (
    <Box>
      <CircularLoading loading={loading} />

      <Grid container spacing={2}>
        {isIncrease ? (
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              {!(location || brand)?.stripe?.customerId ? (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'bold', color: '#096F4D' }}
                          >
                            Enter your credit card information
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'bold', color: '#096F4D' }}
                          >
                            Customer Details
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            required
                            variant="standard"
                            type="text"
                            id="name"
                            name="name"
                            label="Full Name"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.name}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            autoFocus
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="standard"
                            type="email"
                            id="name"
                            name="email"
                            label="Email"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.email}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <MuiTelInput
                            fullWidth
                            required
                            variant="standard"
                            id="phone"
                            name="phone"
                            label="Phone Number"
                            onChange={(newValue: any) =>
                              setCustomerForm({
                                ...customerForm,
                                phone: newValue,
                              })
                            }
                            value={customerForm?.phone}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            onlyCountries={['US']}
                            defaultCountry="US"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 'bold', color: '#096F4D' }}
                          >
                            Address Details
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="standard"
                            type="text"
                            id="line1"
                            name="line1"
                            label="Company Name, Street, PO Box"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.address?.line1}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            variant="standard"
                            fullWidth
                            type="text"
                            id="line2"
                            name="line2"
                            label="Apartment, Suite, Unit, Building"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.address?.line2}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="standard"
                            required
                            type="text"
                            id="city"
                            name="city"
                            label="City"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.address?.city}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="standard"
                            type="text"
                            id="state"
                            name="state"
                            label="State"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.address?.state}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <CountrySelectMenu
                            value={{
                              code: 'US',
                              label: 'United States',
                              phone: '1',
                              suggested: true,
                            }}
                            disabled
                            shrink
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="standard"
                            type="text"
                            id="postalCode"
                            name="postal_code"
                            label="Postal Code"
                            onChange={handleOnChangeCustomerForm}
                            value={customerForm?.address?.postal_code}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                          />
                        </Grid>

                        <Grid
                          item
                          xs={12}
                          sx={{
                            justifyContent: 'center',
                            display: 'flex',
                            width: '100%',
                          }}
                        >
                          <PrimaryButton
                            title="Add Credit Card"
                            type="button"
                            startIcon={<AddCardIcon />}
                            handleOnClick={handleOpenTermsModal}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ) : null}

              {(location || brand)?.stripe?.customerId ? (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 'bold', color: '#096F4D' }}
                      >
                        Select the payment method
                      </Typography>
                    </Grid>

                    {paymentLoading ? (
                      <Grid
                        item
                        xs={12}
                        sx={{ justifyContent: 'center', display: 'flex' }}
                      >
                        <Spinner />
                      </Grid>
                    ) : paymentMethods?.length > 0 ? (
                      paymentMethods.map((payment: any, index: number) => {
                        const selected =
                          payment.id === formValues?.paymentMethod;

                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={`payment-card-${index}`}
                            sx={{
                              position: 'relative',
                            }}
                          >
                            {selected ? (
                              <Box
                                component="div"
                                sx={{
                                  position: 'absolute',
                                  top: '6px',
                                  zIndex: 1,
                                  right: '-12px',
                                  float: 'right',
                                  margin: '1px',
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{
                                    color: DEFAULT_COLOR_THEME,
                                    bgcolor: 'InfoBackground',
                                    borderRadius: '50%',
                                  }}
                                />
                              </Box>
                            ) : null}

                            <Card
                              sx={{
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                                border: selected ? 'solid' : '',
                                borderColor: selected
                                  ? DEFAULT_COLOR_THEME
                                  : '',
                                borderWidth: 'medium',
                                zIndex: 0,
                              }}
                              onClick={() =>
                                setFormValues({
                                  ...formValues,
                                  paymentMethod: payment.id,
                                })
                              }
                            >
                              <CardHeader
                                avatar={
                                  <IconContext.Provider
                                    value={{ size: '40px' }}
                                  >
                                    {renderCCIcon(payment.card.brand)}
                                  </IconContext.Provider>
                                }
                                title={`${buildCardBrand(payment.card)} ●●●● ${
                                  payment.card.last4
                                }`}
                                subheader={`Expires ${payment.card.exp_month}/${payment.card.exp_year}`}
                                titleTypographyProps={{ fontWeight: 'bold' }}
                              />
                            </Card>
                          </Grid>
                        );
                      })
                    ) : (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          justifyContent: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 'bold', textAlign: 'center' }}
                        >
                          No Payment Methods
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'center' }}
                        >
                          Click Add Payment Method button to add a new payment
                          method
                        </Typography>
                      </Grid>
                    )}

                    <Grid
                      item
                      xs={12}
                      sx={{
                        justifyContent: 'center',
                        width: '100%',
                        display: 'flex',
                      }}
                    >
                      <PrimaryButton
                        title="Add Payment Method"
                        type="button"
                        handleOnClick={handleAddPaymentMethod}
                        loading={loading}
                        startIcon={<AddCardIcon />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        ) : null}

        {isNoCharge ? (
          <Grid item xs={12} sm={6}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {`Total Budget `}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    {toCurrency(
                      'USD',
                      isNoCharge && isCampaignLevelBudget
                        ? currentCampaignBudget
                        : (isCampaignLevelBudget
                            ? formValues?.campaignTemplate
                            : formValues?.adSetTemplate
                          )?.totalBudget,
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {`Future Budget `}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    {toCurrency(
                      'USD',
                      isNoCharge && isCampaignLevelBudget
                        ? formValues?.campaignTemplate?.futureBudget > 0
                          ? formValues?.campaignTemplate?.futureBudget
                          : futureCampaignBudget
                        : (isCampaignLevelBudget
                            ? formValues?.campaignTemplate
                            : formValues?.adSetTemplate
                          )?.futureBudget,
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12} sm={6}>
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {`Media Budget ${isIncrease ? 'Increase' : 'Decrease'}`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    {toCurrency(
                      'USD',
                      !isIncrease
                        ? Math.abs(
                            (isCampaignLevelBudget
                              ? formValues?.campaignTemplate
                              : formValues?.adSetTemplate
                            )?.totalCharge -
                              (isCampaignLevelBudget
                                ? formValues?.campaignTemplate
                                : formValues?.adSetTemplate
                              )?.totalCharge *
                                (brand?.budgetPercentageFee / 100),
                          )
                        : (isCampaignLevelBudget
                            ? formValues?.campaignTemplate
                            : formValues?.adSetTemplate
                          )?.totalCharge -
                            (isCampaignLevelBudget
                              ? formValues?.campaignTemplate
                              : formValues?.adSetTemplate
                            )?.totalCharge *
                              (brand?.budgetPercentageFee / 100),
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight:
                          buildChargingPlatform() === 'Charged Immediately'
                            ? 'bold'
                            : '',
                        color:
                          buildChargingPlatform() === 'Charged Immediately'
                            ? 'red'
                            : '',
                      }}
                    >
                      {`(${buildChargingPlatform()})`}
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {`Subscription Fee ${
                        isIncrease ? 'Increase' : 'Decrease'
                      }`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    {toCurrency(
                      'USD',
                      !isIncrease
                        ? Math.abs(
                            (isCampaignLevelBudget
                              ? formValues?.campaignTemplate
                              : formValues?.adSetTemplate
                            )?.totalCharge *
                              (brand?.budgetPercentageFee / 100),
                          )
                        : (isCampaignLevelBudget
                            ? formValues?.campaignTemplate
                            : formValues?.adSetTemplate
                          )?.totalCharge *
                            (brand?.budgetPercentageFee / 100),
                    )}
                  </Grid>

                  <Grid item xs={4}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: !isIncrease ? '' : 'bold',
                        color: !isIncrease ? '' : 'red',
                      }}
                    >
                      {!isIncrease
                        ? '(Credit will apply to the next invoice)'
                        : '(Charged Immediately)'}
                    </Typography>
                  </Grid>

                  {isIncrease && creditBalance?.ending_balance < 0 ? (
                    <>
                      <Grid item xs={4}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Account Credit
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        xs={4}
                        sx={{ justifyContent: 'center', display: 'flex' }}
                      >
                        <Typography variant="body1">
                          {toCurrency('USD', calculatedAppliedBalance())}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} />
                    </>
                  ) : null}

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {`Total${!isIncrease ? ' Decrease' : ''}`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    sx={{ justifyContent: 'center', display: 'flex' }}
                  >
                    <Typography variant="body1">
                      {toCurrency(
                        'USD',
                        Math.abs(
                          (isCampaignLevelBudget
                            ? formValues?.campaignTemplate
                            : formValues?.adSetTemplate
                          )?.totalCharge,
                        ),
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={4} />

                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Box
                      component="img"
                      src={PoweredByStripe}
                      alt=""
                      width={130}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {isIncrease ? (
              <FormControlLabel
                control={
                  <Checkbox size="small" defaultChecked={confirmPayment} />
                }
                label={
                  <Fragment>
                    <Typography
                      variant="body2"
                      sx={{ color: 'red', fontWeight: 600 }}
                    >
                      {buildPaymentAgreementLabel()}
                    </Typography>
                  </Fragment>
                }
                value={confirmPayment}
                onChange={(e: any) => {
                  setConfirmPayment(e.target.checked);
                }}
                sx={{ mt: 2 }}
              />
            ) : null}
          </Grid>
        )}
      </Grid>

      <StripePaymentTermsModal
        open={openTermsModal}
        onClose={handleOpenTermsModal}
        onAgree={handleCreateCustomer}
        title="Advertising Services Agreement"
        loading={createCustomerLoading}
      />

      <StripePaymentUIModal
        open={openPaymentModal}
        onClose={handleClosePaymentModal}
        clientSecret={clientSecret}
        transactionType={transactionType}
        allowRedirect={false}
      />
    </Box>
  );
};

export default DiyBillingSummaryV2;
